import { LRUCache } from 'lru-cache';
import { formatDistance } from 'date-fns';
import { sv, pt, de } from 'date-fns/locale';

export type TiptappAd = {
    _id: string;
    title: string;
    country: Market;
    imageUrls: string[];
    type: 'paaw' | 'giaw' | 'h2t' | 'other';
    state: AdState;
    done: number | null;
    firstPublished: number | null;
    completedIn?: string; // Human readable locale-dependant string, like "35 minutes"
};

const adsCache = new LRUCache<string, TiptappAd>({ max: 500 });

export async function useTiptappAd(adId: string, locale: Ref<string>) {
    const {
        public: { tiptappApiBaseUrl },
    } = useRuntimeConfig();

    const tiptappAd = useState<TiptappAd | null>(`tiptapp-ad-${adId}`, () => null);

    await callOnce(async () => {
        const cahcedAd = adsCache.get(adId);

        if (cahcedAd) {
            tiptappAd.value = cahcedAd;
        } else {
            const response = await $fetch<any>(`${tiptappApiBaseUrl}/v1/ads/${adId}`);

            const ad = {
                _id: response.item._id as string,
                title: response.item.title as string,
                imageUrls: [response.item.img, ...response.item.imgs] as string[],
                country: response.item.country.toLowerCase() as Market,
                type: tiptappAdTypeToName(response.item.type),
                state: tiptappAdStateToName(response.item.state),
                done: response.item.done || null,
                firstPublished: response.item.firstPublished || null,
            };

            if (ad.state === 'verified' || ad.state === 'done') {
                adsCache.set(adId, ad);
            }

            tiptappAd.value = ad;
        }
    });

    // Note, we don't want to store `completedIn` in the cache, as it's locale dependent
    if (tiptappAd.value) {
        tiptappAd.value.completedIn = tiptappAdCompletedIn(tiptappAd.value, locale.value);
    }

    return { tiptappAd };
}

function tiptappAdTypeToName(type: number): 'paaw' | 'giaw' | 'h2t' | 'other' {
    return (
        {
            1: 'paaw' as const,
            2: 'giaw' as const,
            4: 'h2t' as const,
        }[type] || ('other' as const)
    );
}

function tiptappAdStateToName(state: number): AdState {
    return (
        {
            0: 'published' as const,
            1: 'hidden' as const,
            2: 'assigned' as const,
            3: 'done' as const,
            4: 'verified' as const,
            5: 'pickedUp' as const,
        }[state] || ('removed' as const)
    );
}

function tiptappAdCompletedIn(ad: TiptappAd, locale: string) {
    if (ad && ad.firstPublished && ad.done) {
        const dateFnsLocale = { sv, de, pt }[locale];
        let opts = {}; // en locale is default
        if (dateFnsLocale) {
            opts = { locale: dateFnsLocale };
        }

        const completedIn = ad.done - ad.firstPublished;
        // Return empty string if completedIn is more than 6 hours
        if (completedIn < 0 || completedIn > 6 * 60 * 60 * 1000) {
            return '';
        }

        const distance = formatDistance(0, ad.done - ad.firstPublished, opts);
        const prefix = { sv: 'på', de: 'in', pt: 'em' }[locale] || 'in';
        return `${prefix} ${distance}`;
    } else {
        return '';
    }
}
